<template>
  <div class="flex flex-col h-full overflow-y-hidden bg-white mx-10 mt-5 p-5">
    <div class="border-b pb-5 flex items-center w-full">
      <h1 class="text-2xl font-bold">Questionnaire Steps</h1>
      <a class="text-blue-500 hover:text-blue-700 cursor-pointer ml-auto" size="small" @click="newStep()">New Step</a>
    </div>

    <div class="flex flex-1 h-full overflow-y-hidden pt-5">
      <div class="w-[30%] max-w-[450px] min-w-[250px] mr-5 h-full overflow-y-scroll p-5 border">
        <ItemContainer v-for="step in steps" :key="step.id" @click="setStep(step)">
          {{ step.title }}
        </ItemContainer>
      </div>
      <div class="grow border p-5 h-full overflow-y-scroll">
        <FormKit
          v-if="selectedStepStr"
          v-slot="{ value: _value, state: { valid } }"
          :actions="false"
          type="form"
          @submit="handleSubmit"
          v-model="selectedStep"
          :config="{
            classes: {
              inner: 'max-w-screen-md',
            },
          }"
        >
          <div v-if="selectedStep.id" class="flex justify-end">
            <LSButton
              class="flex justify-end"
              variant="outline"
              type="danger"
              size="small"
              @click.prevent.stop="removeStep(selectedStep.title, selectedStep.id)"
            >
              <template #icon>
                <TrashIcon />
              </template>
            </LSButton>
          </div>
          <FormKit type="hidden" name="id" />
          <FormKit type="text" name="code" label="code" validation="required:trim" />
          <FormKit type="text" name="title" label="title" />
          <FormKit
            v-slot="{ index }"
            type="repeater"
            inner-class="max-w-screen-md"
            name="questions"
            :draggable="true"
            :insert-control="true"
          >
            <FormKit :label="`Question: ${index + 1}`" type="select" :options="questionOptions" name="question_id" />
          </FormKit>
          <FormKit type="submit" :disabled="!valid || selectedStep.questions.length < 1" />
        </FormKit>
        <div v-else>
          <div class="">Select a step or <a class="cursor-pointer underline" @click="newStep()">create a new step</a></div>
        </div>
      </div>
    </div>
    <DialogModal
      :show="dialogData.isOpen"
      :title="dialogData.title"
      :description="dialogData.description"
      :action-buttons="[
        { onClick: dialogData.accept, type: 'danger', label: 'Delete' },
        { onClick: dialogData.cancel, type: 'secondary', label: 'Cancel' },
      ]"
    >
    </DialogModal>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useAdminStore } from '@/admin/adminStore'
import { computed, ref, watch } from 'vue'
import type { ApiStep } from '@/admin/types'
import ItemContainer from '@/questionnaire/components/TemplateEditor/ItemContainer.vue'
import LSButton from '@/components/LSButton.vue'
import TrashIcon from '@/assets/icons/24/trash.svg?component'
import DialogModal from '@/components/DialogModal.vue'

const adminStore = useAdminStore()
const { steps, questions } = storeToRefs(adminStore)

const selectedStep = ref<FormSchema>({ code: '', questions: [], title: '' })
const selectedStepStr = ref('')
const dialogData = ref({
  isOpen: false,
  description: '',
  title: '',
  accept: () => {},
  cancel: () => {
    dialogData.value.isOpen = false
  },
})

const closeModal = () => {
  dialogData.value = {
    isOpen: false,
    description: '',
    title: '',
    accept: () => {},
    cancel: () => {
      dialogData.value.isOpen = false
    },
  }
}

const removeStep = (stepTitle: string, id: string) => {
  dialogData.value.isOpen = true
  dialogData.value.title = 'Remove Step'
  dialogData.value.description = `would you like to remove the step "${stepTitle}" ?`
  dialogData.value.accept = () => {
    adminStore.removeStep(id)
    closeModal()
  }
}

const newStep = () => {
  selectedStepStr.value = 'newStep'
  selectedStep.value = { code: '', title: '', questions: [] }
}

const setStep = (step: ApiStep) => {
  const { id, code, title, question_ids } = step
  const schema = { id, code, title, questions: [] as { question_id: string }[] }
  schema.questions = question_ids?.map(id => ({ question_id: id })) ?? []
  selectedStep.value = schema
  if (code) {
    selectedStepStr.value = code
  }
}

const questionOptions = computed(() => {
  return questions.value.map(question => ({
    label: question.title,
    value: question.id,
  }))
})

type FormSchema = {
  id?: string
  code: string
  title: string
  questions: {
    question_id: string
  }[]
}

const handleSubmit = async (data: FormSchema) => {
  const { id, code, title } = data
  const step: ApiStep = { id, code, title, question_ids: [] }
  step.question_ids = data.questions.map(q => q.question_id)
  selectedStepStr.value = code

  if (selectedStepStr.value === 'newStep') {
    selectedStepStr.value = code
  }
  await adminStore.saveStep(step)
}

watch(
  () => steps.value,
  () => updateStepFormStore()
)

const updateStepFormStore = () => {
  const activeStep = steps.value?.find(({ code }) => code === selectedStepStr.value)

  if (activeStep) {
    setStep(activeStep)
  } else {
    selectedStepStr.value = ''
  }
}
</script>
